import { FC, useContext, useMemo, useState } from 'react';
import { debounce, isNull, isUndefined, orderBy } from 'lodash-es';
import { CgChevronDown } from 'react-icons/cg';
import {
  Box,
  Button,
  Flex,
  Heading,
  Input,
  Menu,
  Spinner,
  Stack,
  Table,
  Text,
} from '@chakra-ui/react';

import { UseOpenCloseReturn } from '~/core/hooks/useOpenClose';
import {
  useRedBookFamilies,
  useRedBookMakes,
  useRedBookVehicles,
  useRedBookVehicleUsedPrice,
  useRedBookVehicleUsedPriceCalculated,
  useRedBookYearGroups,
  useSaveRedBookData,
} from '~/core/shared/api/plugin';
import { Dialog, EventCapture } from '~/core/shared/components';
import { useEventHandler } from '~/core/shared/hooks/useEventHandler';
import { Tooltip } from '~/core/shared/lib/chakraUi/components';
import {
  Nullable,
  RedBookFamily,
  RedBookMake,
  RedBookVehicle,
  RedBookVehicleUsedPrice,
  RedBookVehicleUsedPriceCalculatedResponse,
  RedBookYearGroup,
} from '~/core/shared/types';
import {
  ApplicationContext,
  DocumentDetailsViewActionContext,
} from '../Layout.component';

export const RedBookModal: FC = () => {
  const { actionContexts, applicationUuid } = useContext(ApplicationContext);

  const {
    action: { key },
  } = useContext(DocumentDetailsViewActionContext);

  const redbookModalApi: UseOpenCloseReturn = actionContexts[key].modal;

  const [selectedMake, setSelectedMake] = useState<RedBookMake>();
  const [selectedFamily, setSelectedFamily] = useState<RedBookFamily>();
  const [selectedYearGroup, setSelectedYearGroup] =
    useState<RedBookYearGroup>();
  const [selectedVehicle, setSelectedVehicle] = useState<RedBookVehicle>();
  const [generateValuation, setGenerateValuation] = useState(false);

  const [makeSearch, setMakeSearch] = useState('');
  const [familySearch, setFamilySearch] = useState('');
  const [yearGroupSearch, setYearGroupSearch] = useState('');

  const { redBookData: makesData, isLoading: isLoadingMakes } = useRedBookMakes(
    { uuid: applicationUuid },
    { enabled: redbookModalApi.open },
  );
  const { redBookData: familiesData, isLoading: isLoadingFamilies } =
    useRedBookFamilies(
      { makeId: selectedMake?.id, uuid: applicationUuid },
      { enabled: !!selectedMake },
    );
  const { redBookData: yearGroupsData, isLoading: isLoadingYearGroups } =
    useRedBookYearGroups(
      { familyId: selectedFamily?.id, uuid: applicationUuid },
      { enabled: !!selectedFamily },
    );
  const { redBookData: vehiclesData, isLoading: isLoadingVehicles } =
    useRedBookVehicles(
      {
        makeId: selectedMake?.id,
        familyId: selectedFamily?.id,
        yearGroupId: selectedYearGroup?.id,
        uuid: applicationUuid,
      },
      { enabled: !!selectedYearGroup },
    );
  const {
    redBookData: vehicleUsedPriceData,
    isLoading: isLoadingVehicleUsedPrice,
  } = useRedBookVehicleUsedPrice(
    { redBookCode: selectedVehicle?.redbookcode, uuid: applicationUuid },
    { enabled: !!selectedVehicle },
  );
  const vehicleUsedPrice: RedBookVehicleUsedPrice | undefined =
    vehicleUsedPriceData?.results?.[0];

  const {
    redBookData: calculatedData,
    isLoading: isLoadingVehicleUsedPriceCalculated,
  } = useRedBookVehicleUsedPriceCalculated(
    {
      redBookCode: selectedVehicle?.redbookcode,
      vehicleYear: selectedVehicle?.releaseyear,
      uuid: applicationUuid,
    },
    { enabled: generateValuation && !!selectedVehicle },
  );
  const vehicleUsedPriceCalculated:
    | RedBookVehicleUsedPriceCalculatedResponse
    | undefined = generateValuation ? calculatedData : undefined;

  const { saveRedBookData, isPending: isLoadingRedBookSaveData } =
    useSaveRedBookData();

  const formatDate = (item: RedBookYearGroup) => {
    const date = new Date(item.year, item.month);
    return `${date.toLocaleString('default', { month: 'short' })} ${date.getFullYear()}`;
  };

  const makes = useMemo(
    () =>
      makesData?.results?.filter((x) =>
        x.name.toLowerCase().includes(makeSearch.toLowerCase()),
      ),
    [makesData?.results, makeSearch],
  );
  const families = useMemo(
    () =>
      familiesData?.results?.filter((x) =>
        x.name.toLowerCase().includes(familySearch.toLowerCase()),
      ),
    [familiesData?.results, familySearch],
  );
  const yearGroups = useMemo(() => {
    const sorted = orderBy(
      yearGroupsData?.results ?? [],
      ['year', 'month'],
      ['desc', 'desc'],
    );
    return sorted.length > 0
      ? sorted.filter((x) =>
          formatDate(x).toLowerCase().includes(yearGroupSearch.toLowerCase()),
        )
      : undefined;
  }, [yearGroupsData?.results, yearGroupSearch]);
  const vehicles = vehiclesData?.results;

  const toDollarFormat = (value: Nullable<number>): string => {
    if (!isNull(value)) {
      return value.toLocaleString('en-AU', {
        currency: 'AUD',
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
        style: 'currency',
      });
    }
    return 'N/A';
  };

  const getAutoCalcResult = (priceType: 'wholesale' | 'retail') =>
    vehicleUsedPriceCalculated?.autoCalcResults?.find(
      (r) => r.priceType === priceType,
    );

  const getHighestPrice = (
    priceKey: 'wholesale' | 'retail',
  ): Nullable<number> => getAutoCalcResult(priceKey)?.results?.asnew ?? null;

  const getGoodPrice = (priceKey: 'wholesale' | 'retail'): Nullable<number> =>
    getAutoCalcResult(priceKey)?.results?.good ?? null;

  const handleSaveRedBookData = useEventHandler(async (): Promise<void> => {
    const data: any = {
      uuid: applicationUuid,
      make: selectedMake,
      family: selectedFamily,
      yearGroup: selectedYearGroup,
      vehicle: selectedVehicle,
      vehicleUsedPrice,
      vehicleUsedPriceCalculated,
      km: vehicleUsedPriceCalculated?.ExpectedKm,
      wholesaleHigh: getHighestPrice('wholesale'),
      WholesaleGood: getGoodPrice('wholesale'),
      retailHigh: getHighestPrice('retail'),
      retailGood: getGoodPrice('retail'),
    };
    try {
      await saveRedBookData(data);
      redbookModalApi.onClose();
    } catch {
      // Error is handled in useSaveRedBookData
    }
  });

  const selectMake = (item: RedBookMake) => {
    setSelectedMake(item);
    setSelectedFamily(undefined);
    setSelectedYearGroup(undefined);
    setSelectedVehicle(undefined);
    setGenerateValuation(false);
    setFamilySearch('');
    setYearGroupSearch('');
  };

  const selectFamily = (item: RedBookFamily) => {
    setSelectedFamily(item);
    setSelectedYearGroup(undefined);
    setSelectedVehicle(undefined);
    setGenerateValuation(false);
    setYearGroupSearch('');
  };

  const selectYearGroup = (item: RedBookYearGroup) => {
    setSelectedYearGroup(item);
    setSelectedVehicle(undefined);
    setGenerateValuation(false);
  };

  const selectVehicle = (item: RedBookVehicle) => {
    setSelectedVehicle(item);
    setGenerateValuation(false);
  };

  const onSearchMake = debounce(
    (event: any) => setMakeSearch(event.target.value),
    500,
  );
  const onSearchFamily = debounce(
    (event: any) => setFamilySearch(event.target.value),
    500,
  );
  const onSearchYearGroup = debounce(
    (event: any) => setYearGroupSearch(event.target.value),
    500,
  );

  const renderValuationTableBody = () => (
    <Table.Body>
      <Table.Row>
        <Table.Cell height={0}>Valuation Average KM</Table.Cell>
        <Table.Cell colSpan={2} height={0}>
          {vehicleUsedPriceCalculated?.ExpectedKm}
        </Table.Cell>
      </Table.Row>
      <Table.Row>
        <Table.Cell height={0} />
        <Table.Cell height={0}>High</Table.Cell>
        <Table.Cell height={0}>Good</Table.Cell>
      </Table.Row>
      <Table.Row>
        <Table.Cell height={0}>Valuation Wholesale</Table.Cell>
        <Table.Cell height={0}>
          {toDollarFormat(getHighestPrice('wholesale'))}
        </Table.Cell>
        <Table.Cell height={0}>
          {toDollarFormat(getGoodPrice('wholesale'))}
        </Table.Cell>
      </Table.Row>
      <Table.Row>
        <Table.Cell height={0}>Valuation Retail</Table.Cell>
        <Table.Cell height={0}>
          {toDollarFormat(getHighestPrice('retail'))}
        </Table.Cell>
        <Table.Cell height={0}>
          {toDollarFormat(getGoodPrice('retail'))}
        </Table.Cell>
      </Table.Row>
    </Table.Body>
  );

  const valuationText = (price: RedBookVehicleUsedPrice | undefined) =>
    isUndefined(price) ? (
      <Stack>
        <Text>Used price not found to generate valuation.</Text>
      </Stack>
    ) : (
      ''
    );

  return (
    <Dialog
      header="RedBook - Valuation"
      onOpenChange={redbookModalApi.onOpenChange}
      open={redbookModalApi.open}
      size="lg"
    >
      <Flex direction="column">
        <Stack gap={4}>
          <Menu.Root>
            <Menu.Trigger asChild>
              <Button
                borderColor="#CBD5E0"
                borderRadius="md"
                color="#000000"
                disabled={!makes}
                fontSize="14px"
                loading={isLoadingMakes}
                size="md"
                variant="outline"
                width="100%"
              >
                {selectedMake?.name || 'Select make'}
                <CgChevronDown />
              </Button>
            </Menu.Trigger>
            <Menu.Positioner>
              <Menu.Content maxH={200} overflowY="auto">
                <EventCapture captureOnKeyDown>
                  <Input
                    onChange={onSearchMake}
                    pl={2}
                    placeholder="Search"
                    pr={2}
                  />
                </EventCapture>
                {makes?.map((item) => (
                  <Menu.Item
                    key={item.id}
                    minH="48px"
                    onClick={() => selectMake(item)}
                    value={item.name}
                  >
                    <span>{item.name}</span>
                  </Menu.Item>
                ))}
              </Menu.Content>
            </Menu.Positioner>
          </Menu.Root>
          <Menu.Root>
            <Menu.Trigger asChild>
              <Button
                borderColor="#CBD5E0"
                borderRadius="md"
                color="#000000"
                disabled={!families}
                fontSize="14px"
                loading={isLoadingFamilies}
                size="md"
                variant="outline"
                width="100%"
              >
                {selectedFamily?.name || 'Select family'}
                <CgChevronDown />
              </Button>
            </Menu.Trigger>
            <Menu.Positioner>
              <Menu.Content maxH={200} overflowY="auto">
                <EventCapture captureOnKeyDown>
                  <Input
                    onChange={onSearchFamily}
                    pl={2}
                    placeholder="Search"
                    pr={2}
                  />
                </EventCapture>
                {families?.map((item) => (
                  <Menu.Item
                    key={item.id}
                    minH="48px"
                    onClick={() => selectFamily(item)}
                    value={item.name}
                  >
                    <span>{item.name}</span>
                  </Menu.Item>
                ))}
              </Menu.Content>
            </Menu.Positioner>
          </Menu.Root>
          <Menu.Root>
            <Menu.Trigger asChild>
              <Button
                borderColor="#CBD5E0"
                borderRadius="md"
                color="#000000"
                disabled={!yearGroups}
                fontSize="14px"
                loading={isLoadingYearGroups}
                size="md"
                variant="outline"
                width="100%"
              >
                {selectedYearGroup
                  ? formatDate(selectedYearGroup)
                  : 'Select year group'}
                <CgChevronDown />
              </Button>
            </Menu.Trigger>
            <Menu.Positioner>
              <Menu.Content maxH={200} overflowY="auto">
                <EventCapture captureOnKeyDown>
                  <Input
                    onChange={onSearchYearGroup}
                    pl={2}
                    placeholder="Search"
                    pr={2}
                  />
                </EventCapture>
                {yearGroups?.map((item) => (
                  <Menu.Item
                    key={item.id}
                    minH="48px"
                    onClick={() => selectYearGroup(item)}
                    value={`year-${item.id}`}
                  >
                    <span>{formatDate(item)}</span>
                  </Menu.Item>
                ))}
              </Menu.Content>
            </Menu.Positioner>
          </Menu.Root>
          <Menu.Root>
            <Menu.Trigger asChild>
              <Button
                borderColor="#CBD5E0"
                borderRadius="md"
                color="#000000"
                disabled={!vehicles}
                fontSize="14px"
                loading={isLoadingVehicles}
                size="md"
                variant="outline"
                width="100%"
              >
                {selectedVehicle?.description || 'Select Vehicle'}
                <CgChevronDown />
              </Button>
            </Menu.Trigger>
            <Menu.Positioner>
              <Menu.Content maxH={200} overflowY="auto">
                {vehicles?.map((item) => (
                  <Menu.Item
                    key={item.redbookcode}
                    minH="48px"
                    onClick={() => selectVehicle(item)}
                    value={item.redbookcode}
                  >
                    <span>{item.description.toString()}</span>
                  </Menu.Item>
                ))}
              </Menu.Content>
            </Menu.Positioner>
          </Menu.Root>

          <Box>
            <Heading as="h3" fontSize="18px" lineHeight="24px" mb={4}>
              RedBook Valuation
            </Heading>
            <Text fontSize="md" fontWeight="bold">
              Vehicle New Price:{' '}
              {isLoadingVehicleUsedPrice && <Spinner size="xs" />}
              {!isLoadingVehicleUsedPrice && (
                <>
                  {selectedVehicle?.newprice &&
                    toDollarFormat(selectedVehicle.newprice)}
                  {selectedVehicle?.newprice === null && 'No price found.'}
                  {!selectedVehicle && 'N/A'}
                </>
              )}
            </Text>
          </Box>

          {isLoadingVehicleUsedPriceCalculated && <Spinner />}

          {vehicleUsedPriceCalculated && (
            <Box>
              <Heading as="h3" fontSize="md" lineHeight="24px" mb={4}>
                RedBook Valuation Calculated:
              </Heading>
              <Table.Root size="sm">
                <Table.Header backgroundColor="gray" color="white">
                  <Table.Row>
                    <Table.ColumnHeader colSpan={3}>
                      Valuation Prices
                    </Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                {renderValuationTableBody()}
              </Table.Root>
            </Box>
          )}

          <Stack direction="row" justifyContent="end">
            <Tooltip content={valuationText(vehicleUsedPrice)} showArrow>
              <span>
                <Button
                  colorPalette="blue"
                  disabled={!vehicleUsedPrice}
                  fontSize="md"
                  fontWeight="normal"
                  loading={isLoadingVehicleUsedPriceCalculated}
                  onClick={() => setGenerateValuation(true)}
                >
                  Generate Used Valuation
                </Button>
              </span>
            </Tooltip>
            <Button
              colorPalette="green"
              disabled={!selectedVehicle?.newprice}
              fontSize="md"
              fontWeight="normal"
              loading={isLoadingRedBookSaveData}
              onClick={handleSaveRedBookData}
            >
              Save {vehicleUsedPriceCalculated ? 'Used Valuation' : 'New Price'}
            </Button>
            <Button
              colorPalette="gray"
              fontSize="md"
              fontWeight="normal"
              onClick={redbookModalApi.onClose}
              variant="outline"
            >
              Close
            </Button>
          </Stack>
        </Stack>
      </Flex>
    </Dialog>
  );
};
